/**
 * Session Permissions Hook
 * 
 * This hook provides a centralized way to check user permissions within a group session.
 * It fetches the user's role for a specific session and returns a set of permission flags
 * that can be used for conditional rendering and access control throughout the application.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/core/api/supabase';
import { GroupSessionRole } from '@/types';

/**
 * Permission flags for various actions within a group session
 */
export type SessionPermissions = {
  // User role information
  userRole: GroupSessionRole | null;
  isOwner: boolean;
  isFacilitator: boolean; // NEW: Expose app-level facilitator status
  isDocumentor?: boolean; // Role-specific flag
  
  // Session-level permissions
  canViewSession: boolean;
  canEditSession: boolean;
  canAddActivity: boolean;
  canManageMembers: boolean;
  canDeleteSession: boolean;
  
  // Activity-level permissions
  canViewActivities: boolean;
  canManageActivities: boolean;
  canEditActivity: boolean;
  canDeleteActivity: boolean;
  canReorderActivities: boolean;
  
  // Artifact permissions
  canUploadArtifact: boolean;
  canUploadArtifacts: boolean; // Alternative name for compatibility
  canViewArtifacts: boolean;
  canEditArtifacts: boolean;
  canDeleteArtifacts: boolean;
  canReorderArtifacts: boolean;
  canViewArtifactsOverride: boolean; // NEW: Absolute override for facilitators
  
  // Participant management permissions
  canViewParticipants: boolean;
  canManageParticipants: boolean;
  
  // Report permissions
  canViewReports: boolean;
  canViewReport: boolean;
  canGenerateReports: boolean;
  canGenerateReport: boolean;
  canEditReport: boolean;
  canDeleteReport: boolean;
  canDeleteReports: boolean;
  canEditActivityReports: boolean;
  
  // Settings permissions
  canEditSettings: boolean;
  canDuplicateSession: boolean;
  
  // Feedback permissions (available to all participants)
  canProvideFeedback: boolean;
  canEvaluateActivity: boolean;
  
  // Loading state
  isLoading: boolean;
}

/**
 * Hook to get user permissions for a specific group session
 * 
 * @param sessionId The ID of the group session to check permissions for
 * @returns A set of permission flags for the current user in the specified session
 */
export function useSessionPermissions(sessionId?: string): SessionPermissions {
  const [permissions, setPermissions] = useState<SessionPermissions>({
    // Default to no permissions while loading
    userRole: null,
    isOwner: false,
    isFacilitator: false, // Initialize facilitator status as false
    isDocumentor: false,
    
    // Session-level permissions
    canViewSession: false,
    canEditSession: false,
    canAddActivity: false,
    canManageMembers: false,
    canDeleteSession: false,
    
    // Activity-level permissions
    canViewActivities: false,
    canManageActivities: false,
    canEditActivity: false,
    canDeleteActivity: false,
    canReorderActivities: false,
    
    // Artifact permissions
    canUploadArtifact: false,
    canUploadArtifacts: false,
    canViewArtifacts: false,
    canEditArtifacts: false,
    canDeleteArtifacts: false,
    canReorderArtifacts: false,
    canViewArtifactsOverride: false, // NEW: Initialize override flag
    
    // Participant management permissions
    canViewParticipants: false,
    canManageParticipants: false,
    
    // Report permissions
    canViewReports: true, // All users can view reports by default
    canViewReport: true, // All users can view reports by default
    canGenerateReports: false,
    canGenerateReport: false,
    canEditReport: false,
    canDeleteReport: false,
    canDeleteReports: false,
    canEditActivityReports: false,
    
    // Settings permissions
    canEditSettings: false,
    canDuplicateSession: false,
    
    // Feedback permissions
    canProvideFeedback: true, // All members can provide feedback
    canEvaluateActivity: true, // All members can evaluate activities
    
    // Loading state
    isLoading: true,
  });
  
  useEffect(() => {
    // Reset loading state when sessionId changes
    setPermissions(prev => ({ ...prev, isLoading: true }));
    
    // Don't fetch if no sessionId is provided
    if (!sessionId) {
      setPermissions(prev => ({ ...prev, isLoading: false }));
      return;
    }
    
    async function fetchUserRole() {
      try {
        // First get the current authenticated user
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {

          setPermissions(prev => ({ ...prev, isLoading: false }));
          return;
        }

        // Convert slug to UUID if needed
        const isUUID = sessionId ? /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sessionId) : false;
        let actualSessionId = sessionId || '';

        if (!isUUID) {
          // sessionId is a slug, need to look up the UUID
          const { data: sessionData, error: sessionError } = await supabase
            .from('group_sessions')
            .select('id')
            .eq('slug', sessionId)
            .single();

          if (sessionError || !sessionData) {
            console.error('Failed to resolve slug to UUID:', sessionError);
            setPermissions(prev => ({ ...prev, isLoading: false }));
            return;
          }

          actualSessionId = sessionData.id;
        }

        // Get user's role for this session using the UUID
        const { data, error } = await supabase
          .from('group_session_members')
          .select('role')
          .eq('group_session_id', actualSessionId)
          .eq('user_id', user.id)
          .single();
        
        if (error) {

          setPermissions(prev => ({ ...prev, isLoading: false }));
          return;
        }
        
        if (!data) {

          setPermissions(prev => ({ ...prev, isLoading: false }));
          return;
        }
        
        // Normalize role to ensure case consistency
        const role = data.role?.toLowerCase() as GroupSessionRole;
        
        // CRITICAL FIX: Ensure proper role detection
        // Convert both to lowercase for comparison to prevent case sensitivity issues
        const isOwnerRole = role === 'owner' || role === GroupSessionRole.OWNER?.toLowerCase();
        const isEditorRole = role === 'editor' || role === GroupSessionRole.EDITOR?.toLowerCase();
        
        // Log role information for debugging
        console.log('Session Permission Role Check:', {
          userRole: role,
          rawDataRole: data.role,
          isOwnerRole,
          isEditorRole,
          sessionId
        });
        
        // Define permission shorthands
        const hasEditPermission = isOwnerRole || isEditorRole;
        const isOwner = isOwnerRole;

        // Check if user has facilitator or admin role (app-level)
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
          
        // Check if the user is a facilitator or admin at the app level
        const isFacilitator = !profileError && profileData?.role === 'facilitator';
        const isAdmin = !profileError && profileData?.role === 'admin';
        
        // Admin users have all permissions
        if (isAdmin) {
          setPermissions({
            userRole: 'owner' as GroupSessionRole, // Treat admin as owner for permissions
            isOwner: true,
            isFacilitator: true,
            isDocumentor: false,
            canViewSession: true,
            canEditSession: true,
            canAddActivity: true,
            canManageMembers: true,
            canDeleteSession: true,
            canViewActivities: true,
            canManageActivities: true,
            canEditActivity: true,
            canDeleteActivity: true,
            canReorderActivities: true,
            canUploadArtifact: true,
            canUploadArtifacts: true,
            canViewArtifacts: true,
            canEditArtifacts: true,
            canDeleteArtifacts: true,
            canReorderArtifacts: true,
            canViewArtifactsOverride: true,
            canViewParticipants: true,
            canManageParticipants: true,
            canViewReports: true,
            canViewReport: true,
            canGenerateReports: true,
            canGenerateReport: true,
            canEditReport: true,
            canDeleteReport: true,
            canDeleteReports: true,
            canEditActivityReports: true,
            canEditSettings: true,
            canDuplicateSession: true,
            canProvideFeedback: true,
            canEvaluateActivity: true,
            isLoading: false,
          });
          return;
        }
        
        // Debug logging for role detection
        console.log('ROLE CHECK:', {
          profileData,
          profileError,
          isFacilitator,
          isAdmin,
          role: profileData?.role,
          userId: user.id
        });
        
        // Set permissions based on role
        const isDocumentorRole = role === 'documentor' || role === GroupSessionRole.DOCUMENTOR?.toLowerCase();
        
        setPermissions({
          // User role information
          userRole: role,
          isOwner,
          isFacilitator, // CRITICAL FIX: Pass the facilitator status to the component
          isDocumentor: isDocumentorRole,
          
          // Session-level permissions
          canViewSession: true, // All members can view the session
          canEditSession: isOwner || isFacilitator, // Facilitators can edit sessions
          canAddActivity: hasEditPermission || isFacilitator, // Facilitators can add activities
          // Allow either session owners or app-level facilitators to manage members
          canManageMembers: isOwner || isFacilitator,
          canDeleteSession: isOwner, // Only owners can delete sessions
          
          // Activity-level permissions
          canViewActivities: true, // All members can view activities
          canManageActivities: isOwner || hasEditPermission || isFacilitator,
          canEditActivity: hasEditPermission || isFacilitator, // CRITICAL FIX: Facilitators can edit activities
          canDeleteActivity: isOwner || isFacilitator, // Facilitators can delete activities
          canReorderActivities: isOwner || hasEditPermission || isFacilitator,
          
          // Artifact permissions
          canUploadArtifact: isOwner || hasEditPermission || isFacilitator, // CRITICAL FIX: Facilitators can upload artifacts
          canUploadArtifacts: isOwner || hasEditPermission || isFacilitator, // Alternative name
          // CRITICAL FIX: Always allow facilitators to view artifacts
          canViewArtifacts: isOwner || isFacilitator || ![GroupSessionRole.PARTICIPANT, GroupSessionRole.VIEWER].includes(role),
          canEditArtifacts: isOwner || hasEditPermission || isFacilitator,
          canDeleteArtifacts: isOwner || isFacilitator,
          canReorderArtifacts: isOwner || hasEditPermission || isFacilitator,
          // Force enable artifact viewing for facilitators
          canViewArtifactsOverride: isOwner || isFacilitator, // New override flag for absolute control
          
          // Participant management permissions
          canViewParticipants: true, // All members can view participants
          canManageParticipants: isOwner || isFacilitator,
          
          // Report permissions
          canViewReports: true, // Everyone can view reports
          canViewReport: true, // Everyone can view reports
          canGenerateReports: isOwner || hasEditPermission || isFacilitator,
          canGenerateReport: isOwner || hasEditPermission || isFacilitator, // CRITICAL FIX: Facilitators can generate reports
          canEditReport: isOwner || hasEditPermission || isFacilitator, // Facilitators can edit reports
          canDeleteReport: isOwner || isFacilitator, // Owners and facilitators can delete reports
          canDeleteReports: isOwner || isFacilitator,
          canEditActivityReports: isOwner || hasEditPermission || isFacilitator,
          
          // Settings permissions
          canEditSettings: isOwner || isFacilitator,
          canDuplicateSession: isOwner || hasEditPermission || isFacilitator,
          
          // Feedback permissions
          canProvideFeedback: true, // Everyone can provide feedback
          canEvaluateActivity: true, // Everyone can evaluate activities
          
          // Not loading anymore
          isLoading: false,
        });
      } catch (error) {

        setPermissions(prev => ({ ...prev, isLoading: false }));
      }
    }
    
    fetchUserRole();
    
    // Clean up function
    return () => {
      // No cleanup needed as we're using async/await and not subscribing to anything
    };
  }, [sessionId]);
  
  return permissions;
}

/**
 * Helper to check if a specific permission is loading or denied
 * Returns an object with isLoading and isDenied flags
 * 
 * @example
 * const { canEditSession, isLoading } = useSessionPermissions(sessionId);
 * const { isLoading: editLoading, isDenied: editDenied } = checkPermission(canEditSession, isLoading);
 * 
 * if (editLoading) return <LoadingSpinner />;
 * if (editDenied) return <AccessDenied />;
 * return <EditForm />;
 */
export function checkPermission(
  permission: boolean,
  isLoading: boolean
): { isLoading: boolean; isDenied: boolean } {
  return {
    isLoading,
    isDenied: !isLoading && !permission,
  };
}

export default useSessionPermissions;
